// src/components/familyoffice/MarketValuations.jsx
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, TrendingUp, TrendingDown, Minus, AlertCircle, Search, ChevronDown, ChevronUp } from "lucide-react";
import { supabase } from "../../lib/supabaseClient";

export default function MarketValuations() {
  const [familyOfficeId, setFamilyOfficeId] = useState(null);
  const [families, setFamilies] = useState([]);
  const [familiesData, setFamiliesData] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedFamilies, setExpandedFamilies] = useState({});

  // Charger le family office de l'utilisateur connecté
  useEffect(() => {
    const loadFamilyOffice = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          setLoading(false);
          return;
        }

        // Récupérer le profile pour vérifier le rôle
        const { data: profile, error } = await supabase
          .from("profiles")
          .select("role")
          .eq("id", user.id)
          .single();

        if (error) {
          console.error("Erreur chargement profile:", error);
          setLoading(false);
          return;
        }
        
        // Si c'est un family_office, son ID est son family_office_id
        if (profile?.role === 'family_office') {
          setFamilyOfficeId(user.id);
        } else {
          console.log("Utilisateur n'est pas un family office");
          setLoading(false);
        }
      } catch (error) {
        console.error("Erreur chargement family office:", error);
        setLoading(false);
      }
    };

    loadFamilyOffice();
  }, []);

  // Fonctions de calcul
  const calculateTotalValue = (artworks) => {
    return artworks.reduce((sum, art) => {
      const val = art.current_estimated_value || art.valuation_estimated_value || art.estimated_value || art.value || 0;
      return sum + val;
    }, 0);
  };

  const calculateTotalPurchase = (artworks) => {
    return artworks.reduce((sum, art) => {
      const price = art.acquisition_price_eur || art.acquisition_price || art.price || 0;
      return sum + price;
    }, 0);
  };

  const calculateGlobalChange = (artworks) => {
    const totalValue = calculateTotalValue(artworks);
    const totalPurchase = calculateTotalPurchase(artworks);
    return totalPurchase > 0 ? ((totalValue - totalPurchase) / totalPurchase) * 100 : 0;
  };

  // Charger données d'une famille
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadAllFamiliesData = useCallback(async (familiesList) => {
    const dataByFamily = {};
    
    for (const family of familiesList) {
      try {
        let clientIds = [];

        // Option 1 : client_profile_id direct
        if (family.client_profile_id) {
          clientIds = [family.client_profile_id];
        } else {
          // Option 2 : chercher via family_clients
          const { data: familyClients } = await supabase
            .from("family_clients")
            .select("client_id")
            .eq("family_id", family.id);
          
          if (familyClients && familyClients.length > 0) {
            clientIds = familyClients.map(fc => fc.client_id);
          }
        }

        if (clientIds.length === 0) {
          console.log(`Famille ${family.label} sans clients`);
          dataByFamily[family.id] = {
            artworks: [],
            totalValue: 0,
            totalPurchase: 0,
            change: 0
          };
          continue;
        }

        // Récupérer collections pour tous les clients
        const { data: collections, error: collError } = await supabase
          .from("collections")
          .select("id")
          .in("client_id", clientIds);

        if (collError) {
          console.error(`Erreur collections famille ${family.label}:`, collError);
          dataByFamily[family.id] = {
            artworks: [],
            totalValue: 0,
            totalPurchase: 0,
            change: 0
          };
          continue;
        }

        const collectionIds = collections?.map(c => c.id) || [];

        if (collectionIds.length === 0) {
          console.log(`Aucune collection pour famille ${family.label}`);
          dataByFamily[family.id] = {
            artworks: [],
            totalValue: 0,
            totalPurchase: 0,
            change: 0
          };
          continue;
        }

        // Récupérer œuvres
        const { data: artworks, error: artError } = await supabase
          .from("artworks_secure")
          .select("id, title, artist, year, current_estimated_value, valuation_estimated_value, estimated_value, value, valuation_low, valuation_high, last_valuation_date, last_estimation_date, image_url, acquisition_price_eur, acquisition_price, acquisition_currency, price")
          .in("collection_id", collectionIds)
          .order("title");

        if (artError) {
          console.error(`Erreur artworks famille ${family.label}:`, artError);
        }

        const artworksList = artworks || [];
        dataByFamily[family.id] = {
          artworks: artworksList,
          totalValue: calculateTotalValue(artworksList),
          totalPurchase: calculateTotalPurchase(artworksList),
          change: calculateGlobalChange(artworksList)
        };
      } catch (error) {
        console.error(`Erreur données famille ${family.label}:`, error);
        dataByFamily[family.id] = {
          artworks: [],
          totalValue: 0,
          totalPurchase: 0,
          change: 0
        };
      }
    }
    
    setFamiliesData(dataByFamily);
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Charger les familles
  const loadFamilies = useCallback(async () => {
    if (!familyOfficeId) return;
    
    setLoading(true);
    try {
      const { data: familiesData, error } = await supabase
        .from("families")
        .select("id, label, client_profile_id")
        .eq("family_office_id", familyOfficeId)
        .eq("status", "active")
        .order("label");

      if (error) throw error;
      
      setFamilies(familiesData || []);
      
      if (familiesData && familiesData.length > 0) {
        await loadAllFamiliesData(familiesData);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Erreur chargement familles:", error);
      setFamilies([]);
      setLoading(false);
    }
  }, [familyOfficeId, loadAllFamiliesData]);

  // Déclencher chargement
  useEffect(() => {
    if (familyOfficeId) {
      loadFamilies();
    }
  }, [familyOfficeId, loadFamilies]);

  const getValueChange = (artwork) => {
    const purchasePrice = artwork.acquisition_price_eur || artwork.acquisition_price || artwork.price || 0;
    const currentValue = artwork.current_estimated_value || artwork.valuation_estimated_value || artwork.estimated_value || artwork.value || 0;
    
    if (!purchasePrice || !currentValue || purchasePrice === 0) return null;
    
    const change = ((currentValue - purchasePrice) / purchasePrice) * 100;
    return change;
  };

  const toggleFamily = (familyId) => {
    setExpandedFamilies(prev => ({
      ...prev,
      [familyId]: !prev[familyId]
    }));
  };

  // Stats globales
  const globalStats = families.reduce((acc, family) => {
    const data = familiesData[family.id];
    if (data) {
      acc.totalValue += data.totalValue;
      acc.totalPurchase += data.totalPurchase;
    }
    return acc;
  }, { totalValue: 0, totalPurchase: 0 });

  const globalChange = globalStats.totalPurchase > 0 
    ? ((globalStats.totalValue - globalStats.totalPurchase) / globalStats.totalPurchase) * 100 
    : 0;

  // Filtrer familles
  const filteredFamilies = families.filter(family =>
    family.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    familiesData[family.id]?.artworks?.some(artwork =>
      artwork.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      artwork.artist?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="bg-white border-b border-[#E8E8E8]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Link 
            to="/family-office/dashboard" 
            className="inline-flex items-center gap-2 text-sm text-[#666] hover:text-[#1A1A1A] transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au tableau de bord
          </Link>

          <div>
            <h1 className="text-4xl font-serif text-[#1A1A1A] mb-2">
              Valorisations de Marché
            </h1>
            <p className="text-lg text-[#666] font-light">
              Vue d'ensemble par famille
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <div className="bg-white rounded-xl border border-[#E8E8E8] p-6">
            <p className="text-sm text-[#666] mb-2 font-light">Valeur Totale Portefeuille</p>
            <p className="text-3xl font-serif text-[#1A1A1A]">
              {globalStats.totalValue.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
            </p>
            <p className="text-xs text-[#999] mt-2 font-light">{families.length} familles gérées</p>
          </div>

          <div className="bg-white rounded-xl border border-[#E8E8E8] p-6">
            <p className="text-sm text-[#666] mb-2 font-light">Investissement Total</p>
            <p className="text-3xl font-serif text-[#1A1A1A]">
              {globalStats.totalPurchase.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
            </p>
          </div>

          <div className="bg-white rounded-xl border border-[#E8E8E8] p-6">
            <p className="text-sm text-[#666] mb-2 font-light">Performance Globale</p>
            <div className="flex items-center gap-2">
              <p className={`text-3xl font-serif ${globalChange >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {globalChange >= 0 ? '+' : ''}{globalChange.toFixed(1)}%
              </p>
              {globalChange > 0 && <TrendingUp className="w-6 h-6 text-emerald-600" />}
              {globalChange < 0 && <TrendingDown className="w-6 h-6 text-red-600" />}
              {globalChange === 0 && <Minus className="w-6 h-6 text-[#666]" />}
            </div>
          </div>
        </div>

        <div className="mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#999]" />
            <input
              type="text"
              placeholder="Rechercher une famille, une œuvre ou un artiste..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3.5 border border-[#E8E8E8] rounded-lg bg-white text-[#1A1A1A] font-light focus:outline-none focus:ring-2 focus:ring-[#1A1A1A] focus:border-transparent"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-8 h-8 border-2 border-[#E8E8E8] border-t-[#1A1A1A] rounded-full animate-spin"></div>
            <p className="text-[#666] mt-4 font-light">Chargement des valorisations...</p>
          </div>
        ) : filteredFamilies.length === 0 ? (
          <div className="bg-white rounded-xl border border-[#E8E8E8] p-12 text-center">
            <AlertCircle className="w-12 h-12 text-[#999] mx-auto mb-4" />
            <p className="text-[#666] font-light">
              {searchTerm ? "Aucune famille ne correspond à votre recherche" : "Aucune famille trouvée"}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredFamilies.map((family) => {
              const data = familiesData[family.id] || { artworks: [], totalValue: 0, totalPurchase: 0, change: 0 };
              const isExpanded = expandedFamilies[family.id];
              
              return (
                <div key={family.id} className="bg-white rounded-xl border border-[#E8E8E8] overflow-hidden">
                  <div 
                    className="p-6 cursor-pointer hover:bg-[#FAFAFA] transition-colors"
                    onClick={() => toggleFamily(family.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <h2 className="text-2xl font-serif text-[#1A1A1A]">{family.label}</h2>
                          {isExpanded ? 
                            <ChevronUp className="w-5 h-5 text-[#666]" /> : 
                            <ChevronDown className="w-5 h-5 text-[#666]" />
                          }
                        </div>
                        
                        <div className="grid grid-cols-3 gap-6">
                          <div>
                            <p className="text-xs text-[#999] mb-1 font-light">Valeur</p>
                            <p className="text-lg font-serif text-[#1A1A1A]">
                              {data.totalValue.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
                            </p>
                          </div>
                          
                          <div>
                            <p className="text-xs text-[#999] mb-1 font-light">Investissement</p>
                            <p className="text-lg font-serif text-[#1A1A1A]">
                              {data.totalPurchase.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
                            </p>
                          </div>
                          
                          <div>
                            <p className="text-xs text-[#999] mb-1 font-light">Performance</p>
                            <div className="flex items-center gap-2">
                              <p className={`text-lg font-serif ${data.change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {data.change >= 0 ? '+' : ''}{data.change.toFixed(1)}%
                              </p>
                              {data.change > 0 && <TrendingUp className="w-4 h-4 text-emerald-600" />}
                              {data.change < 0 && <TrendingDown className="w-4 h-4 text-red-600" />}
                            </div>
                          </div>
                        </div>
                        
                        <p className="text-xs text-[#999] mt-3 font-light">
                          {data.artworks.length} œuvre{data.artworks.length > 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  </div>

                  {isExpanded && data.artworks.length > 0 && (
                    <div className="border-t border-[#E8E8E8] bg-[#FAFAFA] p-6">
                      <div className="space-y-4">
                        {data.artworks.map((artwork) => {
                          const change = getValueChange(artwork);
                          const purchasePrice = artwork.acquisition_price_eur || artwork.acquisition_price || artwork.price || 0;
                          const currentValue = artwork.current_estimated_value || artwork.valuation_estimated_value || artwork.estimated_value || artwork.value || 0;
                          const lastDate = artwork.last_valuation_date || artwork.last_estimation_date;
                          
                          return (
                            <div key={artwork.id} className="bg-white rounded-lg border border-[#E8E8E8] p-4">
                              <div className="flex items-start gap-4">
                                {artwork.image_url ? (
                                  <img 
                                    src={artwork.image_url} 
                                    alt={artwork.title}
                                    className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                                  />
                                ) : (
                                  <div className="w-20 h-20 bg-[#F5F5F5] rounded-lg flex items-center justify-center flex-shrink-0">
                                    <span className="text-[#999] text-xs">Pas d'image</span>
                                  </div>
                                )}

                                <div className="flex-1 min-w-0">
                                  <h4 className="text-lg font-serif text-[#1A1A1A] mb-1 truncate">
                                    {artwork.title || "Sans titre"}
                                  </h4>
                                  <p className="text-xs text-[#666] mb-3 font-light">
                                    {artwork.artist || "Artiste inconnu"} {artwork.year && `• ${artwork.year}`}
                                  </p>
                                  
                                  <div className="grid grid-cols-3 gap-4">
                                    <div>
                                      <p className="text-xs text-[#999] mb-1 font-light">Prix d'achat</p>
                                      <p className="text-sm font-serif text-[#1A1A1A]">
                                        {purchasePrice > 0 ? 
                                          purchasePrice.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }) 
                                          : '—'}
                                      </p>
                                    </div>

                                    <div>
                                      <p className="text-xs text-[#999] mb-1 font-light">Valeur actuelle</p>
                                      <p className="text-sm font-serif text-[#1A1A1A]">
                                        {currentValue > 0 ? 
                                          currentValue.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }) 
                                          : '—'}
                                      </p>
                                    </div>

                                    <div>
                                      <p className="text-xs text-[#999] mb-1 font-light">Variation</p>
                                      {change !== null ? (
                                        <div className="flex items-center gap-1">
                                          <p className={`text-sm font-serif ${change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                            {change >= 0 ? '+' : ''}{change.toFixed(1)}%
                                          </p>
                                          {change > 0 && <TrendingUp className="w-3 h-3 text-emerald-600" />}
                                          {change < 0 && <TrendingDown className="w-3 h-3 text-red-600" />}
                                        </div>
                                      ) : (
                                        <p className="text-sm font-serif text-[#999]">—</p>
                                      )}
                                    </div>
                                  </div>

                                  {lastDate && (
                                    <p className="text-xs text-[#999] mt-2 font-light">
                                      Dernière estimation : {new Date(lastDate).toLocaleDateString('fr-FR')}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-10 bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-serif text-[#1A1A1A] mb-2">Note sur les estimations</h4>
              <p className="text-sm text-[#666] font-light leading-relaxed">
                Ces estimations sont calculées à partir des données de marché disponibles et des algorithmes d'analyse d'Artkiv. 
                Elles constituent des indicateurs de tendance mais ne remplacent pas une expertise formelle par un professionnel certifié. 
                Pour une valorisation officielle (assurance, succession, vente), nous recommandons de faire appel à un expert agréé.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}